import { inject, Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { BehaviorSubject, firstValueFrom, Observable, filter, map } from 'rxjs';
import { Settings } from '../../../models/settings.model';
import { TaxCode } from '../../../models/tax-code.model';

/**
 * Wire-format payload for creating/updating a tax code. `percentage` is sent as a
 * fixed-precision decimal string (matching the backend DecimalField) rather than the
 * `number` type used on the parsed `TaxCode` domain model, to avoid float/JSON
 * serialization ambiguity for decimal values.
 */
export interface TaxCodeWriteDto {
  code: string;
  name: string;
  percentage: string;
}

@Injectable({ providedIn: 'root' })
export class SettingsService {
  private http = inject(HttpClient);

  private settingsSubject = new BehaviorSubject<Settings | null>(null);
  public settings$ = this.settingsSubject.asObservable();

  constructor() {
    void this.getSettings();
  }

  /**
   * Get current settings
   * @returns Promise with current settings
   */
  async getSettings(): Promise<void> {
    const settings = await firstValueFrom(
      this.http.get<Settings>('/settings-api/settings/'),
    );
    this.settingsSubject.next(settings);
  }

  /**
   * Patch (partially update) settings
   * @param settings Partial settings object with fields to update
   * @returns Promise with updated settings
   */
  async patchSettings(settings: Partial<Settings>): Promise<void> {
    const updatedSettings = await firstValueFrom(
      this.http.patch<Settings>('/settings-api/settings/', settings),
    );
    this.settingsSubject.next(updatedSettings);
  }

  isMouldingEnabled$(): Observable<boolean> {
    return this.settings$.pipe(
      filter((settings) => settings !== null),
      map((settings) => settings.auto_mould_generation_enabled),
    );
  }

  isPdfDrawingUploadEnabled$(): Observable<boolean> {
    return this.settings$.pipe(
      filter((settings) => settings !== null),
      map((settings) => settings.pdf_drawing_upload_enabled),
    );
  }

  /**
   * Upload file to settings
   * @param file The file to upload
   * @returns Promise with updated settings
   */
  async uploadFile(file: File): Promise<void> {
    const formData = new FormData();
    formData.append('long_form_logo', file);

    const updatedSettings = await firstValueFrom(
      this.http.patch<Settings>('/settings-api/settings/', formData),
    );
    this.settingsSubject.next(updatedSettings);
  }

  isTradeStudyToolFeaturesEnabled$(): Observable<boolean> {
    return this.settings$.pipe(
      filter((settings) => settings !== null),
      map((settings) => settings?.trade_study_tool_features_enabled ?? false),
    );
  }

  isConsumablesEnabled$(): Observable<boolean> {
    return this.settings$.pipe(
      filter((settings) => settings !== null),
      map((settings) => settings?.consumables_enabled ?? false),
    );
  }

  isTokenBillingEnabled$(): Observable<boolean> {
    return this.settings$.pipe(
      filter((settings) => settings !== null),
      map((settings) => settings?.token_billing_enabled ?? false),
    );
  }

  /**
   * Get all tax codes
   * @returns Promise with array of tax codes
   */
  async getTaxCodes(): Promise<TaxCode[]> {
    return await firstValueFrom(
      this.http.get<TaxCode[]>('/quotes-api/tax-codes/'),
    );
  }

  /**
   * Create a new tax code
   * @param taxCode Tax code data
   * @returns Promise with created tax code
   */
  async createTaxCode(taxCode: Partial<TaxCodeWriteDto>): Promise<TaxCode> {
    return await firstValueFrom(
      this.http.post<TaxCode>('/quotes-api/tax-codes/', taxCode),
    );
  }

  /**
   * Update a tax code
   * @param id Tax code ID
   * @param taxCode Partial tax code data
   * @returns Promise with updated tax code
   */
  async updateTaxCode(
    id: number,
    taxCode: Partial<TaxCodeWriteDto>,
  ): Promise<TaxCode> {
    return await firstValueFrom(
      this.http.patch<TaxCode>(`/quotes-api/tax-codes/${id}/`, taxCode),
    );
  }

  /**
   * Delete a tax code
   * @param id Tax code ID
   * @returns Promise
   */
  async deleteTaxCode(id: number): Promise<void> {
    await firstValueFrom(this.http.delete(`/quotes-api/tax-codes/${id}/`));
  }
}
